import React, { useState, useEffect } from "react";
import {
  Award,
  Loader2,
  Coins,
  Check,
} from "lucide-react";

import {
  useApplyRewardPointsMutation,
  useLazyRemoveRewardPointsQuery,
} from "../../../redux/actions/Customer/cartCustomer";

interface Props {
  totalPoints: number;
  maxUsablePoints: number;
  minimumRedeemPoints: number;
  appliedPoints: number;
  discountAmount: number;
  onApply?: (points: number) => void;
  onRemove?: () => void;
}

const RewardPointsCard: React.FC<Props> = ({
  totalPoints,
  maxUsablePoints,
  appliedPoints = 0,
  onApply,
  minimumRedeemPoints = 0,
  discountAmount = 0,
  onRemove,
}) => {
  const [enabled, setEnabled] =
    useState(appliedPoints > 0);

  const [
    applyRewardsAction,
    { isLoading: isApplying },
  ] = useApplyRewardPointsMutation();

  const [
    triggerRemoveRewards,
    { isFetching: isRemoving },
  ] = useLazyRemoveRewardPointsQuery();

  /* =========================================================
     SYNC TO APPLIED POINTS
  ========================================================= */
  useEffect(() => {
    setEnabled(appliedPoints > 0);
  }, [appliedPoints]);

  /* =========================================================
     TOGGLE REWARD POINTS
  ========================================================= */
  const handleToggleStateAction = async () => {
    if (isApplying || isRemoving) return;

    const nextEnabledState = !enabled;

    try {
      if (nextEnabledState) {
        onApply?.(maxUsablePoints);
        setEnabled(true);
      } else {
        onRemove?.();
        setEnabled(false);
      }
    } catch (err) {
      console.error(
        "Reward points toggle error:",
        err
      );
    }
  };

  const hasPointsToUse =
    maxUsablePoints > 0;

  const isBusy =
    isApplying || isRemoving;

  return (
    <div
      className="
        w-full

        p-4
        max-[639px]:p-2.5

        bg-white

        border
        border-slate-100

        rounded-2xl

        shadow-md

        transition-all
        duration-300
      "
    >
      {/* =====================================================
          HEADER
      ===================================================== */}
      <div
        className="
          mb-3.5
          max-[639px]:mb-2.5

          flex
          items-center
          justify-between

          gap-2
        "
      >
        {/* LEFT */}
        <div
          className="
            flex
            items-center

            gap-2.5
            max-[639px]:gap-1.5

            min-w-0
          "
        >
          {/* ICON */}
          <div
            className="
              flex

              h-8
              w-8

              max-[639px]:h-6
              max-[639px]:w-6

              shrink-0

              items-center
              justify-center

              rounded-lg

              bg-gradient-to-br
              from-[#2F5E2E]
              to-emerald-600

              shadow-sm
            "
          >
            <Award
              size={14}
              className="
                text-white

                max-[639px]:w-3.5
                max-[639px]:h-3.5
              "
            />
          </div>

          {/* TITLE */}
          <div className="min-w-0">
            <h2
              className="
                text-sm
                max-[639px]:text-[11px]

                font-bold

                text-slate-800

                tracking-wide

                truncate
              "
            >
              Petland Points
            </h2>

            <p
              className="
                text-[11px]
                max-[639px]:text-[9px]

                text-slate-400

                truncate
              "
            >
              Redeem loyalty points for special savings
            </p>
          </div>
        </div>

        {/* =================================================
            AVAILABLE POINTS
        ================================================= */}
        <div
          className="
            flex
            items-center

            gap-1

            px-2.5
            max-[639px]:px-1.5

            py-0.5

            bg-amber-50

            rounded-full

            border
            border-amber-100

            text-amber-800

            text-[10px]
            max-[639px]:text-[8px]

            font-bold

            shrink-0
          "
        >
          <Coins
            size={11}
            className="
              fill-amber-500
              text-amber-500

              max-[639px]:w-2.5
              max-[639px]:h-2.5
            "
          />

          <span>
            <span
              className="
                font-extrabold

                text-xs
                max-[639px]:text-[9px]
              "
            >
              {totalPoints}
            </span>{" "}
            Points Available
          </span>
        </div>
      </div>

      {/* =====================================================
          REDEMPTION BALANCE
      ===================================================== */}
      <div
        className={`
          flex
          items-center
          justify-between

          gap-2

          border
          rounded-xl

          p-3
          max-[639px]:p-2

          transition-all
          duration-300

          ${
            enabled
              ? "bg-emerald-50/40 border-emerald-100"
              : "bg-slate-50/50 border-slate-100"
          }
        `}
      >
        {/* TEXT */}
        <div
          className="
            space-y-0.5

            pr-2
            max-[639px]:pr-1

            min-w-0
            flex-1
          "
        >
          <p
            className="
              text-[10px]
              max-[639px]:text-[8px]

              font-bold

              text-slate-400

              uppercase
              tracking-wider

              truncate
            "
          >
            Redemption Balance
          </p>

          {hasPointsToUse ? (
            <p
              className="
                text-[11px]
                max-[639px]:text-[9px]

                text-slate-500

                font-medium

                leading-normal
              "
            >
              Use{" "}
              <span
                className="
                  font-extrabold
                  text-slate-800
                "
              >
                {maxUsablePoints} points
              </span>{" "}
              on this order.
            </p>
          ) : (
            <p
              className="
                text-[11px]
                max-[639px]:text-[9px]

                text-slate-400

                font-medium

                leading-normal
              "
            >
              Minimum amount needed{" "}
              <span className="font-bold">
                {minimumRedeemPoints}
              </span>{" "}
              to apply Petland points.
            </p>
          )}
        </div>

        {/* =================================================
            TOGGLE
        ================================================= */}
        {hasPointsToUse && (
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label="Redeem Petland Points"
            disabled={isBusy}
            onClick={
              handleToggleStateAction
            }
            className={`
              relative

              inline-flex

              h-6
              w-11

              max-[639px]:h-5
              max-[639px]:w-9

              shrink-0

              items-center

              rounded-full

              transition-all
              duration-300

              focus:outline-none
              focus:ring-2
              focus:ring-emerald-500/30

              ${
                enabled
                  ? "bg-[#2F5E2E]"
                  : "bg-slate-300"
              }

              ${
                isBusy
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer"
              }
            `}
          >
            <span
              className={`
                flex

                h-4
                w-4

                max-[639px]:h-3.5
                max-[639px]:w-3.5

                items-center
                justify-center

                rounded-full

                bg-white

                shadow-md

                transition-transform
                duration-300

                ${
                  enabled
                    ? "translate-x-6 max-[639px]:translate-x-5"
                    : "translate-x-1"
                }
              `}
            >
              {isBusy && (
                <Loader2
                  size={10}
                  className="
                    animate-spin
                    text-slate-500

                    max-[639px]:w-2.5
                    max-[639px]:h-2.5
                  "
                />
              )}
            </span>
          </button>
        )}
      </div>

      {/* =====================================================
          APPLIED SUCCESS MESSAGE
      ===================================================== */}
      {enabled && (
        <div
          className="
            mt-2.5
            max-[639px]:mt-2

            flex
            items-center

            gap-1.5
            max-[639px]:gap-1

            rounded-lg

            bg-emerald-50

            px-2.5
            max-[639px]:px-2

            py-2
            max-[639px]:py-1.5
          "
        >
          <Check
            size={13}
            className="
              shrink-0

              text-emerald-600

              stroke-[3]

              max-[639px]:w-3
              max-[639px]:h-3
            "
          />

          <p
            className="
              text-[11px]
              max-[639px]:text-[9px]

              text-emerald-800

              font-bold

              leading-tight
            "
          >
            Successfully redeemed{" "}
            <span className="font-extrabold text-emerald-700">
              {Math.round(
                appliedPoints ||
                  maxUsablePoints
              )}{" "}
              points
            </span>{" "}
            ={" "}
            <span className="font-extrabold text-emerald-700">
              AED{" "}
              {discountAmount.toFixed(2)}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default RewardPointsCard;